<template>
  <div>
    <div class="slide-modal-title">Get your instant quote</div>
    <ValidationObserver>
      <form @submit.prevent="onSubmit">
        <div class="form-group control has-icons-right">
          <ValidationProvider
            ref="firstBikeValueObserver"
            v-slot="{ errors }"
            rules="required|min_bike_value:400|max_bike_value:15000"
          >
            <input
              id="replacement-value"
              v-model="bike.firstBikeValue"
              :class="{ 'is-danger': errors[0] }"
              type="number"
              pattern="\d*"
              class="input"
              required
            />
            <label class="form-control-placeholder" for="replacement-value">
              Bike replacement value
            </label>
            <span v-if="errors[0]" class="icon is-danger is-small is-right">
              <font-awesome-icon :icon="['fa', 'exclamation-circle']" />
            </span>
            <span :class="{ 'is-danger': errors[0] }" class="help is-size-10">
              {{ errors[0] }}
            </span>
          </ValidationProvider>
        </div>

        <IsEbike
          ref="isEbike"
          :is-ebike-props="isEbike"
          @isEbikeSelected="(e) => (isEbike = e)"
        />

        <span class="dob-label"> Date of Birth </span>
        <div class="is-flex dob-row">
          <div class="form-group control dob-col">
            <ValidationProvider
              ref="dayObserver"
              v-slot="{ errors }"
              rules="required|min_day:1|max_day:31"
            >
              <div
                class="select is-fullwidth"
                :class="{ 'is-danger': errors[0] }"
              >
                <select id="day" v-model="dateOfBirth.day" required>
                  <option value="" disabled hidden>Day</option>
                  <option v-for="d in days" :key="d" :value="String(d)">
                    {{ d }}
                  </option>
                </select>
              </div>
              <span
                :class="{ 'is-danger': errors[0] }"
                class="help is-size-10"
                >{{ errors[0] }}</span
              >
            </ValidationProvider>
          </div>
          <div class="form-group control dob-col" style="margin: 0 5px">
            <ValidationProvider
              ref="monthObserver"
              v-slot="{ errors }"
              rules="required|min_month:1|max_month:12"
            >
              <div
                class="select is-fullwidth"
                :class="{ 'is-danger': errors[0] }"
              >
                <select id="month" v-model="dateOfBirth.month" required>
                  <option value="" disabled hidden>Month</option>
                  <option
                    v-for="(monthName, i) in months"
                    :key="i"
                    :value="String(i + 1)"
                  >
                    {{ monthName }}
                  </option>
                </select>
              </div>
              <span
                :class="{ 'is-danger': errors[0] }"
                class="help is-size-10"
                >{{ errors[0] }}</span
              >
            </ValidationProvider>
          </div>
          <div class="form-group control dob-col">
            <ValidationProvider
              ref="yearObserver"
              v-slot="{ errors }"
              rules="required|valid_year|min_year|mаx_year"
            >
              <div
                class="select is-fullwidth"
                :class="{ 'is-danger': errors[0] }"
              >
                <!-- Placeholder sits just above 1990 so the dropdown opens
                     scrolled near there (native selects scroll to the selected
                     option) — less scrolling for a typical customer. -->
                <select id="year" v-model="dateOfBirth.year" required>
                  <option v-for="y in yearsRecent" :key="y" :value="String(y)">
                    {{ y }}
                  </option>
                  <option value="" disabled>Year</option>
                  <option v-for="y in yearsOlder" :key="y" :value="String(y)">
                    {{ y }}
                  </option>
                </select>
              </div>
              <span
                :class="{ 'is-danger': errors[0] }"
                class="help is-size-10"
                >{{ errors[0] }}</span
              >
            </ValidationProvider>
          </div>
        </div>

        <div class="form-group control">
          <!-- No rules: the postcode is checked against the search API in
               checkPostcode(), which sets the error here. -->
          <ValidationProvider ref="adddressLookUpObserver" v-slot="{ errors }">
            <PostcodeAddressAutocompleter
              v-model="address"
              :disabled-lookup="true"
            />
            <span :class="{ 'is-danger': errors[0] }" class="help is-size-10">{{
              errors[0]
            }}</span>
          </ValidationProvider>
        </div>

        <ApplyDiscountCode v-if="showDiscountInput" />

        <!-- <br>
        <div @click="showDiscountInput = !showDiscountInput" class="discount">
          Do you have a <span>discount code</span> ?
        </div> -->
        <button
          :class="{
            // isQuoteLoading: the quote is priced by createQuote now, not a
            // separate /price call.
            'is-loading':
              $nuxt.$loading.get() ||
              $store.getters['quote/isPriceLoading'] ||
              $store.getters['quote/isQuoteLoading'] ||
              isCheckingPostcode,
          }"
          type="submit"
          class="button ps-button is-fullwidth call-to-action"
          data-test="get-my-quote"
        >
          Get my quote
        </button>

        <DisplayDiscount class="display-discount" />

        <QuoteTrustPanel />
      </form>
    </ValidationObserver>
  </div>
</template>

<script>
/* eslint-disable no-console */
import ApplyDiscountCode from '@/components/quick-quote/ApplyDiscountCode'
import DisplayDiscount from '@/components/quick-quote/DisplayDiscount'
import QuoteTrustPanel from '@/components/quick-quote/QuoteTrustPanel'
import IsEbike from '@/components/common/IsEbike'
import PostcodeAddressAutocompleter from '@/components/common/PostcodeAddressAutocompleter'

export default {
  name: 'RiskFactors',
  components: {
    ApplyDiscountCode,
    DisplayDiscount,
    QuoteTrustPanel,
    PostcodeAddressAutocompleter,
    IsEbike,
  },
  props: {
    // What the page that opened the panel already knows: the estimator's bike
    // value and age, and the bike type when a landing page supplied one. These
    // seed the fields — they are still shown, and still editable, because the
    // customer has to be able to correct the value their price rests on.
    prefill: {
      type: Object,
      default: null,
    },
  },
  data: () => {
    return {
      bike: {
        firstBikeValue: null,
        firstBikeMake: '',
        firstBikeModel: '',
      },
      dateOfBirth: {
        day: '',
        month: '',
        year: '',
      },
      showDiscountInput: false,
      address: {
        house: '',
        street: '',
        city: '',
        county: '',
        postcode: '',
      },
      isAddressValid: false,
      isCheckingPostcode: false,
      // "No" is the default answer, since most bikes aren't electric. The
      // default lives here rather than in IsEbike so the form, the payload and
      // the store all start from the same value — the child only ever
      // displays what it is given. `bike=electric` overrides it in
      // applyPrefill().
      isEbike: false,
    }
  },

  computed: {
    days() {
      return Array.from({ length: 31 }, (_, i) => i + 1)
    },
    months() {
      return [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
      ]
    },
    // Year list is split around 1990 so the "Year" placeholder can sit just
    // above it — the dropdown then opens scrolled near 1990.
    yearsRecent() {
      // currentYear-16 down to 1991 (newest first, above the placeholder).
      const currentYear = new Date().getFullYear()
      const start = currentYear - 16
      const years = []
      for (let y = start; y > 1990; y--) years.push(y)
      return years
    },
    yearsOlder() {
      // 1990 down to currentYear-100 (below the placeholder).
      const oldest = new Date().getFullYear() - 100
      const years = []
      for (let y = 1990; y >= oldest; y--) years.push(y)
      return years
    },
  },

  watch: {
    prefill: 'applyPrefill',
    // A postcode error belongs to the value it was raised for.
    'address.postcode'() {
      this.$refs.adddressLookUpObserver?.setErrors([])
    },
  },

  created() {
    this.applyPrefill()
  },

  // The panel keeps this step alive between openings, so `created` runs only
  // once. A second opening with different slider values arrives here instead.
  activated() {
    this.applyPrefill()
  },

  methods: {
    // Only fills fields the caller actually knows. The date of birth is
    // derived from the age slider, so it lands on a birthday that gives
    // exactly that age today — close enough to price with, and the customer
    // corrects it to their real one before the quote is created.
    applyPrefill() {
      if (!this.prefill) return

      if (this.prefill.bikeValue) {
        this.bike.firstBikeValue = String(this.prefill.bikeValue)
      }
      if (this.prefill.age) {
        const dob = this.$moment().subtract(Number(this.prefill.age), 'years')
        this.dateOfBirth = {
          day: dob.format('D'),
          month: dob.format('M'),
          year: dob.format('YYYY'),
        }
      }
      // Only ever pre-answered as "yes", and only when the customer arrived
      // from the e-bike landing page. Every other bike type leaves the
      // question for them: a road bike can still be electric.
      if (this.prefill.isEbike === true) {
        this.isEbike = true
      }
    },
    async validate() {
      const day = await this.$refs.dayObserver.validate()
      const month = await this.$refs.monthObserver.validate()
      const year = await this.$refs.yearObserver.validate()
      const postcodeError = await this.checkPostcode()
      const isEbike = this.$refs.isEbike.validate()

      let isValid =
        day.valid && month.valid && year.valid && !postcodeError && isEbike
      const firstBikeValue = await this.$refs.firstBikeValueObserver.validate()
      isValid = isValid && firstBikeValue.valid

      if (isValid) {
        this.$emit('continue', {
          bike: { ...this.bike, isEbike: this.isEbike },
          address: this.address,
          dateOfBirth: this.dateOfBirth,
        })
      } else {
        console.log('not valid')
      }
    },
    // A postcode is accepted only when the search API lists it exactly
    // (spaces and case ignored); the listed form, e.g. "NR2 4TQ", is what
    // goes on the quote. Channel Islands postcodes are excluded, as in the
    // dropdown. Returns the error shown under the field, or '' when valid.
    async checkPostcode() {
      const typed = (this.address.postcode || '')
        .replace(/\s+/g, '')
        .toUpperCase()
      let error = ''
      if (!typed) {
        error = 'Please enter your postcode'
      } else if (/^(GY|JE)/.test(typed)) {
        error = 'Please enter a valid UK postcode'
      } else {
        this.isCheckingPostcode = true
        try {
          const results = await this.$axios.$get(
            `/api/helpers/v1/search-postcode/${encodeURIComponent(typed)}`
          )
          const match = (results || []).find(
            (item) =>
              item.id && item.id.replace(/\s+/g, '').toUpperCase() === typed
          )
          if (match) {
            this.address.postcode = match.id
          } else {
            error = 'Please enter a valid UK postcode'
          }
        } catch (e) {
          // The search endpoint answers 400 for a postcode it doesn't know.
          error = 'Please enter a valid UK postcode'
        } finally {
          this.isCheckingPostcode = false
        }
      }
      // After the await, so the address.postcode watcher can't clear it.
      await this.$nextTick()
      this.$refs.adddressLookUpObserver.setErrors(error ? [error] : [])
      return error
    },
    onSubmit() {
      if (this.isCheckingPostcode) return
      this.validate()
    },
    personalCover() {
      this.$emit('personalCover')
    },
  },
}
</script>

<style lang="scss" scoped>
.fullwidth {
  width: 100%;
}
.discount {
  padding-bottom: 5px;
  span {
    text-decoration: underline;
    cursor: pointer;
  }
}
.display-discount {
  margin-top: 20px;
  color: $ps-primary;
  line-height: 22px;
}
.personal-cover-link {
  // text-align: center;
  margin-top: 20px;
  font-size: 0.75rem;
  span {
    border-bottom: 1px solid;
    cursor: pointer;
  }
}
// Was 8px — error text has to be readable.
.is-size-10 {
  font-size: $ps-text-small;
}
// Same caps label style as every other field label.
.dob-label {
  display: block;
  margin-bottom: 8px;
  font-size: $ps-text-label;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: $ps-muted;
}
.dob-row {
  align-items: flex-start;
}
.dob-col {
  flex: 1;
  min-width: 0;
}
.dob-col .select,
.dob-col .select select {
  width: 100%;
}
// Three-up on a narrow modal — tighten the horizontal padding so "Month"
// clears the chevron.
.dob-col .select select {
  padding-left: 12px;
  padding-right: 34px;
}
.dob-col .select:not(.is-multiple):not(.is-loading)::after {
  right: 14px;
}
// Height, border and radius come from the shared control spec. The old
// `height: 40px` and the `:invalid` border-colour override were why Day and
// Month rendered shorter, and in a different grey, from Year.
.dob-col .select select:invalid {
  color: $ps-muted;
}
</style>
